// src/CONTAINERCONTROL/equipmentCostDetailList.jsx
import React, { useContext, useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BufferService, LoadingContext, M_Table, M_Button, M_MessageBoard, EquipmentCostDetailFilter } from 'src/M_Components'

const detailService = new BufferService('EquipmentCostDetail', 'secondary')

// ── Shared styles ─────────────────────────────────────────────────────────────
const inputStyle = {
    padding: '8px 10px',
    border: '1px solid var(--color-grey-dark2)',
    borderRadius: '6px',
    fontSize: '0.9rem',
    width: '100%',
    boxSizing: 'border-box'
}
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: '6px' }
const labelStyle = { fontSize: '0.85rem', fontWeight: 500, color: 'var(--color-black)' }
const readonlyBoxStyle = {
    display: 'flex', flexDirection: 'column', gap: '6px',
    background: 'var(--color-grey-light)', borderRadius: '6px',
    padding: '10px 14px', fontSize: '0.875rem', color: 'var(--color-grey-dark2)'
}
const modalBodyStyle = { display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '360px', padding: '8px 0' }
const footerStyle = { display: 'flex', gap: '8px', justifyContent: 'flex-end' }

// ── Totals Panel ──────────────────────────────────────────────────────────────
const TotalsPanel = ({ totals, loading, t }) => {
    if (!totals && !loading) return null
    return (
        <div style={{
            width: '200px', flexShrink: 0, background: 'var(--color-white)',
            border: '1px solid var(--color-grey-light)', borderRadius: '8px',
            padding: '16px', height: 'fit-content', marginTop: '4px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
        }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid var(--color-grey-light)' }}>
                {t('Totals')}
            </div>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                    <i className="pi pi-spin pi-spinner" />
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '0.85rem' }}>
                        <span style={{ color: 'var(--color-grey-dark2)' }}>{t('Details')}</span>
                        <span style={{ fontWeight: 600 }}>{totals.TotalDetails}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '0.85rem' }}>
                        <span style={{ color: 'var(--color-grey-dark2)' }}>{t('Est. Cost')}</span>
                        <span style={{ fontWeight: 600, color: 'var(--color-yellow)' }}>{totals.TotalEstimatedCost?.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '0.85rem' }}>
                        <span style={{ color: 'var(--color-grey-dark2)' }}>{t('Approved')}</span>
                        <span style={{ fontWeight: 600, color: 'var(--color-green)' }}>{totals.TotalApprovedCost?.toFixed(2)}</span>
                    </div>
                </>
            )}
        </div>
    )
}

// ── Detail Modal (double-click / view) ────────────────────────────────────────
const DetailModal = ({ open, detail, onClose, onSave, ctx, t }) => {
    const [form, setForm] = useState({})
    const [supplierSearch, setSupplierSearch] = useState('')
    const [supplierOptions, setSupplierOptions] = useState([])
    const [supplierLoading, setSupplierLoading] = useState(false)

    const accountService = new BufferService('Account')

    const initForm = (d) => {
        setForm({
            
            supplierId: d.SupplierId || '',
            supplierName: d.SupplierName || '',
            supplierRef: d.SupplierRef || '',
            estimationDate: d.EstimationDate?.split('T')[0] || '',
            estimationNr: d.EstimationNr || '',
            approvedCost: d.ApprovedCost?.toString() || '',
            approvedDate: d.ApprovedDate?.split('T')[0] || '',
            currencyCode: d.CurrencyCode || '',
            exchange: d.Exchange?.toString() || '',
            description: d.Description || '',
        })
        setSupplierSearch(d.SupplierName || '')
        setSupplierOptions([])
    }

    React.useEffect(() => {
        if (detail) initForm(detail)
    }, [detail])

    if (!open || !detail) return null

    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }))

    const handleSupplierSearch = async (value) => {
        setSupplierSearch(value)
        if (!value.trim()) { setSupplierOptions([]); return }
        setSupplierLoading(true)
        const res = await accountService.filter(`/query?page=1&pagesize=10&filter=contains(AccountCode,'${encodeURIComponent(value)}')`)
        setSupplierLoading(false)
        if (res?.name === 'AxiosError') return
        setSupplierOptions(res?.Items || [])
    }

    const handleSupplierSelect = (account) => {
        set('supplierId', account.Id)
        set('supplierName', account.AccountCode)
        setSupplierSearch(account.AccountCode)
        setSupplierOptions([])
    }

    const handleRefresh = () => {
        initForm(detail)
        ctx.toastMessages('success', '', t('FormRefreshed'))
    }

    const handleSave = async () => {
        ctx.setIsLoad(true)
        const payload = {
            Status: detail.Status,
            SupplierId: form.supplierId || null,
            SupplierName: form.supplierName || null,
            SupplierRef: form.supplierRef || null,
            EstimationDate: form.estimationDate || null,
            EstimationNr: form.estimationNr || null,
            ApprovedCost: form.approvedCost ? parseFloat(form.approvedCost) : null,
            ApprovedDate: form.approvedDate || null,
            CurrencyCode: form.currencyCode || null,
            Exchange: form.exchange ? parseFloat(form.exchange) : null,
            Description: form.description || null,
        }
        const res = await detailService.update(detail.Id, payload)
        ctx.setIsLoad(false)
        if (res?.name === 'AxiosError') {
            ctx.toastMessages('error', res.response?.data?.detail || res.message, t('Error'))
            return
        }
        ctx.toastMessages('success', '', t('UpdatedSuccessfully'))
        onSave()
    }

    return (
        <M_MessageBoard
            open={open}
            onClose={onClose}
            title={`${t('Cost Detail')} — ${detail.EquipmentPrefix}${detail.EquipmentNumber}-${detail.EquipmentCheckDigit}`}
            body={
                <div style={{ ...modalBodyStyle, minWidth: '560px' }}>
                    {/* Action buttons row */}
                    <div style={{
                        display: 'flex', gap: '8px',
                        borderBottom: '1px solid var(--color-grey-light)',
                        paddingBottom: '12px'
                    }}>
                        <M_Button normal hoverEffect label={t('Save')}
                            icon={<i className='pi pi-save' />}
                            backgroundColor={'var(--color-green)'} borderColor={'var(--color-green)'}
                            labelColor={'var(--color-white)'} onClick={handleSave} />
                        <M_Button normal hoverEffect label={t('ButtonCancel')}
                            icon={<i className='pi pi-times' />}
                            backgroundColor={'var(--color-white)'} borderColor={'var(--color-grey-dark2)'}
                            labelColor={'var(--color-grey-dark2)'} onClick={onClose} />
                        {/* <M_Button normal label={t('Cancel All')}
                            icon={<i className='pi pi-ban' />}
                            backgroundColor={'var(--color-grey-light)'} 
                            borderColor={'var(--color-grey-light)'}
                            labelColor={'var(--color-grey-dark2)'}
                            onClick={() => {}}
                            disabled /> */}
                        <M_Button normal hoverEffect label={t('Refresh')}
                            icon={<i className='pi pi-refresh' />}
                            backgroundColor={'var(--color-white)'} borderColor={'var(--color-grey-dark2)'}
                            labelColor={'var(--color-grey-dark2)'} onClick={handleRefresh} />
                        {/* <M_Button normal label={t('Help')}
                            icon={<i className='pi pi-question-circle' />}
                            backgroundColor={'var(--color-white)'} borderColor={'var(--color-grey-dark2)'}
                            labelColor={'var(--color-grey-dark2)'}
                            disabled /> */}
                    </div>

                    {/* Read-only info */}
                    <div style={readonlyBoxStyle}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                            <span>{t('Prefix')}: <b>{detail.EquipmentPrefix}</b></span>
                            <span>{t('Number')}: <b>{detail.EquipmentNumber}</b></span>
                            <span>{t('CD')}: <b>{detail.EquipmentCheckDigit}</b></span>
                            <span>{t('Move')}: <b>{detail.MovementTypeCode}</b></span>
                            <span>{t('Mov. Key')}: <b>{detail.MovementKey ?? '—'}</b></span>
                            <span>{t('Date')}: <b>{detail.MovementDate?.split('T')[0]}</b></span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '8px' }}>
                            <span>{t('Status')}: <b>{detail.Status}</b></span>
                            <span>{t('Checked')}: <b>{detail.Checked ? t('Yes') : t('No')}</b></span>
                            <span>{t('Est. Cost')}: <b>{detail.EstimatedCost}</b></span>
                            <span>{t('Service Name')}: <b>{detail.ServiceTypeName || '—'}</b></span>
                            <span>{t('Service Code')}: <b>{detail.ServiceTypeCode || '—'}</b></span>
                        </div>
                    </div>

                    {/* Editable fields */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        {/* <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Service Code')}</label>
                            <input style={inputStyle} value={form.serviceTypeCode}
                                onChange={e => set('serviceTypeCode', e.target.value)} />
                        </div> */}

                        {/* Supplier autocomplete */}
                        <div style={{ ...fieldStyle, position: 'relative' }}>
                            <label style={labelStyle}>{t('Supplier')}</label>
                            <input style={inputStyle} value={supplierSearch}
                                onChange={e => handleSupplierSearch(e.target.value)}
                                placeholder={t('Search account...')} />
                            {supplierLoading && (
                                <div style={{ position: 'absolute', right: '10px', top: '34px' }}>
                                    <i className='pi pi-spin pi-spinner' style={{ fontSize: '0.8rem' }} />
                                </div>
                            )}
                            {supplierOptions.length > 0 && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
                                    background: 'var(--color-white)', border: '1px solid var(--color-grey-light)',
                                    borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    maxHeight: '200px', overflowY: 'auto'
                                }}>
                                    {supplierOptions.map(acc => (
                                        <div key={acc.Id}
                                            onClick={() => handleSupplierSelect(acc)}
                                            style={{
                                                padding: '8px 12px', cursor: 'pointer', fontSize: '0.875rem',
                                                borderBottom: '1px solid var(--color-grey-light)'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'var(--color-grey-light)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                            {acc.AccountCode}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Supplier Ref')}</label>
                            <input style={inputStyle} value={form.supplierRef}
                                onChange={e => set('supplierRef', e.target.value)} />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Est. Date')}</label>
                            <input type="date" style={inputStyle} value={form.estimationDate}
                                onChange={e => set('estimationDate', e.target.value)} />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Estimation Nr.')}</label>
                            <input style={inputStyle} value={form.estimationNr}
                                onChange={e => set('estimationNr', e.target.value)} />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Currency Code')}</label>
                            <input style={inputStyle} value={form.currencyCode}
                                onChange={e => set('currencyCode', e.target.value)} />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Exchange')}</label>
                            <input type="number" style={inputStyle} value={form.exchange}
                                onChange={e => set('exchange', e.target.value)} />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Approved Cost')}</label>
                            <input type="number" style={inputStyle} value={form.approvedCost}
                                onChange={e => set('approvedCost', e.target.value)} />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Approved Date')}</label>
                            <input type="date" style={inputStyle} value={form.approvedDate}
                                onChange={e => set('approvedDate', e.target.value)} />
                        </div>
                    </div>

                    <div style={fieldStyle}>
                        <label style={labelStyle}>{t('Description')}</label>
                        <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '64px' }}
                            value={form.description}
                            onChange={e => set('description', e.target.value)} />
                    </div>
                </div>
            }
            footer={null}
        />
    )
}

// ── Update All Costs Modal ────────────────────────────────────────────────────
const UpdateAllCostsModal = ({ open, details, onClose, onDone, ctx, t }) => {
    const [form, setForm] = useState({
        supplierId: '', supplierName: '',
        // estimatedCost: '',
        approvedCost: '',
        currencyCode: '', exchange: ''
    })
    const [supplierSearch, setSupplierSearch] = useState('')
    const [supplierOptions, setSupplierOptions] = useState([])
    const [supplierLoading, setSupplierLoading] = useState(false)

    const accountService = new BufferService('Account')

    React.useEffect(() => {
        if (open) {
            setForm({ supplierId: '', supplierName: '', estimatedCost: '', approvedCost: '', currencyCode: '', exchange: '' })
            setSupplierSearch('')
            setSupplierOptions([])
        }
    }, [open])

    if (!open || !details.length) return null
    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }))

    const handleSupplierSearch = async (value) => {
        setSupplierSearch(value)
        if (!value.trim()) { setSupplierOptions([]); return }
        setSupplierLoading(true)
        const res = await accountService.filter(`/query?page=1&pagesize=10&filter=contains(AccountCode,'${encodeURIComponent(value)}')`)
        setSupplierLoading(false)
        if (res?.name === 'AxiosError') return
        setSupplierOptions(res?.Items || [])
    }

    const handleSupplierSelect = (account) => {
        set('supplierId', account.Id)
        set('supplierName', account.AccountCode)
        setSupplierSearch(account.AccountCode)
        setSupplierOptions([])
    }

    const handleSave = async () => {
    //     if (!form.estimatedCost) { 
    //     ctx.toastMessages('error', '', t('Missing required fields'))
    //     return 
    // }
        ctx.setIsLoad(true)
        const payload = {
            IdList: details.map(d => d.Id),
            SupplierId: form.supplierId || null,
            SupplierName: form.supplierName || null,
            // EstimatedCost: form.estimatedCost ? parseFloat(form.estimatedCost) : 0,
            ApprovedCost: form.approvedCost ? parseFloat(form.approvedCost) : null,
            CurrencyCode: form.currencyCode || null,
            Exchange: form.exchange ? parseFloat(form.exchange) : null,
        }
        const res = await detailService.update(`UpdateAllCosts`, payload)
        ctx.setIsLoad(false)
        if (res?.name === 'AxiosError') {
            ctx.toastMessages('error', res.response?.data?.detail || res.message, t('Error'))
            return
        }
        ctx.toastMessages('success', '', t('UpdatedSuccessfully'))
        onDone()
    }

    return (
        <M_MessageBoard
            open={open} onClose={onClose}
            title={`${t('Update All Costs')} — ${details.length} ${t('selected')}`}
            body={
                <div style={modalBodyStyle}>
                    <div style={readonlyBoxStyle}>
                        <span style={{ fontSize: '0.8rem' }}>{t('AppliedToAllSelected')}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div style={{ ...fieldStyle, position: 'relative', gridColumn: '1 / -1' }}>
                            <label style={labelStyle}>{t('Supplier')}</label>
                            <input style={inputStyle} value={supplierSearch}
                                onChange={e => handleSupplierSearch(e.target.value)}
                                placeholder={t('Search account...')} />
                            {supplierLoading && (
                                <div style={{ position: 'absolute', right: '10px', top: '34px' }}>
                                    <i className='pi pi-spin pi-spinner' style={{ fontSize: '0.8rem' }} />
                                </div>
                            )}
                            {supplierOptions.length > 0 && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
                                    background: 'var(--color-white)', border: '1px solid var(--color-grey-light)',
                                    borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    maxHeight: '200px', overflowY: 'auto'
                                }}>
                                    {supplierOptions.map(acc => (
                                        <div key={acc.Id}
                                            onClick={() => handleSupplierSelect(acc)}
                                            style={{
                                                padding: '8px 12px', cursor: 'pointer', fontSize: '0.875rem',
                                                borderBottom: '1px solid var(--color-grey-light)'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'var(--color-grey-light)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                            {acc.AccountCode}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Estimated Cost')}</label>
                            <input type="number" style={inputStyle} value={form.estimatedCost}
                                onChange={e => set('estimatedCost', e.target.value)} />
                        </div> */}
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Approved Cost')}</label>
                            <input type="number" style={inputStyle} value={form.approvedCost}
                                onChange={e => set('approvedCost', e.target.value)} />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Currency Code')}</label>
                            <input style={inputStyle} value={form.currencyCode}
                                onChange={e => set('currencyCode', e.target.value)} />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Exchange')}</label>
                            <input type="number" style={inputStyle} value={form.exchange}
                                onChange={e => set('exchange', e.target.value)} />
                        </div>
                    </div>
                </div>
            }
            footer={
                <span style={footerStyle}>
                    <M_Button normal hoverEffect label={t('ButtonCancel')}
                        backgroundColor={'var(--color-white)'} borderColor={'var(--color-grey-dark2)'}
                        labelColor={'var(--color-grey-dark2)'} onClick={onClose} />
                    <M_Button normal hoverEffect label={t('Update All')}
                        backgroundColor={'var(--color-blue)'} borderColor={'var(--color-blue)'}
                        labelColor={'var(--color-white)'} onClick={handleSave} />
                </span>
            }
        />
    )
}

// ── Approve Cost Modal (individual) ───────────────────────────────────────────
const ApproveCostModal = ({ open, details, onClose, onDone, ctx, t }) => {
    const [index, setIndex] = useState(0)
    const [approvedCost, setApprovedCost] = useState('')
    const [approvedDate, setApprovedDate] = useState(new Date().toISOString().split('T')[0])

    React.useEffect(() => {
        if (open && details.length) {
            setIndex(0)
            setApprovedCost(details[0]?.EstimatedCost?.toString() || '')
            setApprovedDate(new Date().toISOString().split('T')[0])
        }
    }, [open, details])

    if (!open || !details.length) return null
    const current = details[index]
    const isLast = index === details.length - 1

    const handleNext = async () => {
        if (!approvedCost) { ctx.toastMessages('error', '', t('Missing required fields')); return }
        ctx.setIsLoad(true)
        const res = await detailService.patch(`ApproveCost/${current.Id}`, {
            ApprovedCost: parseFloat(approvedCost), ApprovedDate: approvedDate
        })
        ctx.setIsLoad(false)
        if (res?.name === 'AxiosError') { ctx.toastMessages('error', res.response?.data?.detail || res.message, t('Error')); return }
        ctx.toastMessages('success', '', t('CostApproved'))
        if (isLast) { onDone(); return }
        const next = details[index + 1]
        setIndex(i => i + 1)
        setApprovedCost(next?.EstimatedCost?.toString() || '')
        setApprovedDate(new Date().toISOString().split('T')[0])
    }

    return (
        <M_MessageBoard
            open={open} onClose={onClose}
            title={<span>{t('Approve Cost')} — {index + 1}/{details.length}</span>}
            body={
                <div style={{ ...modalBodyStyle, minWidth: '480px' }}>
                    <div style={readonlyBoxStyle}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                            <span>{t('Prefix')}: <b>{current.EquipmentPrefix}</b></span>
                            <span>{t('Number')}: <b>{current.EquipmentNumber}</b></span>
                            <span>{t('CD')}: <b>{current.EquipmentCheckDigit}</b></span>
                            <span>{t('Move')}: <b>{current.MovementTypeCode}</b></span>
                            <span>{t('Mov. Key')}: <b>{current.MovementKey ?? '—'}</b></span>
                            <span>{t('Date')}: <b>{current.MovementDate?.split('T')[0]}</b></span>
                        </div>
                        <div style={{ borderTop: '1px solid var(--color-grey-dark2)', margin: '8px 0' }} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <span>{t('Status')}: <b>{current.Status}</b></span>
                            <span>{t('Checked')}: <b>{current.Checked ? t('Yes') : t('No')}</b></span>
                            <span>{t('Service')}: <b>{current.ServiceTypeCode}</b></span>
                            <span>{t('Supplier')}: <b>{current.SupplierName}</b></span>
                            <span>{t('Est. Cost')}: <b>{current.EstimatedCost}</b></span>
                            <span>{t('Supplier Ref')}: <b>{current.SupplierRef || '—'}</b></span>
                            <span>{t('Est. Date')}: <b>{current.EstimationDate?.split('T')[0] || '—'}</b></span>
                            <span>{t('Est. Nr.')}: <b>{current.EstimationNr || '—'}</b></span>
                            <span>{t('Currency')}: <b>{current.CurrencyCode || '—'}</b></span>
                            <span>{t('Exchange')}: <b>{current.Exchange ?? '—'}</b></span>
                        </div>
                        {current.Description && (
                            <div style={{ marginTop: '4px' }}>
                                <span>{t('Description')}: <b>{current.Description}</b></span>
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Approved Cost')}</label>
                            <input type="number" style={inputStyle} value={approvedCost}
                                onChange={e => setApprovedCost(e.target.value)} />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('Approved Date')}</label>
                            <input type="date" style={inputStyle} value={approvedDate}
                                onChange={e => setApprovedDate(e.target.value)} />
                        </div>
                    </div>
                </div>
            }
            footer={
                <span style={footerStyle}>
                    <M_Button normal hoverEffect label={t('ButtonCancel')}
                        backgroundColor={'var(--color-white)'} borderColor={'var(--color-grey-dark2)'}
                        labelColor={'var(--color-grey-dark2)'} onClick={onClose} />
                    <M_Button normal hoverEffect
                        label={isLast ? t('Approve') : `${t('Approve')} & ${t('Next')}`}
                        backgroundColor={'var(--color-green)'} borderColor={'var(--color-green)'}
                        labelColor={'var(--color-white)'} onClick={handleNext} />
                </span>
            }
        />
    )
}

// ── Approve All Costs Modal ───────────────────────────────────────────────────
const ApproveAllCostsModal = ({ open, details, onClose, onDone, ctx, t }) => {
    const [approvedDate, setApprovedDate] = useState(new Date().toISOString().split('T')[0])

    React.useEffect(() => {
        if (open) setApprovedDate(new Date().toISOString().split('T')[0])
    }, [open])

    if (!open || !details.length) return null
    

    const handleApprove = async () => {
        ctx.setIsLoad(true)
        const res = await detailService.patch(`ApproveAllCosts/`, {IdList: details.map(d => d.Id),  ApprovedDate: approvedDate })
        ctx.setIsLoad(false)
        if (res?.name === 'AxiosError') { ctx.toastMessages('error', res.response?.data?.detail || res.message, t('Error')); return }
        ctx.toastMessages('success', '', t('AllCostsApproved'))
        onDone()
    }

    return (
        <M_MessageBoard
            open={open} onClose={onClose}
            title={`${t('Approve All Costs')} — ${details.length} ${t('selected')}`}
            body={
                <div style={{ ...modalBodyStyle, minWidth: '480px' }}>
                    <div style={readonlyBoxStyle}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                            <span>{t('Prefix')}: <b>*</b></span>
                            <span>{t('Number')}: <b>*</b></span>
                            <span>{t('Move')}: <b>*</b></span>
                            <span>{t('Mov. Key')}: <b>0</b></span>
                            <span>{t('Cost Key')}: <b>0</b></span>
                        </div>
                        <div style={{ borderTop: '1px solid var(--color-grey-dark2)', margin: '8px 0' }} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <span>{t('Service Code')}: <b>*</b></span>
                            <span>{t('Service Name')}: <b>—</b></span>
                            <span>{t('Supplier')}: <b>*</b></span>
                            <span>{t('Supplier Ref')}: <b>—</b></span>
                            <span>{t('Est. Cost')}: <b>—</b></span>
                            <span>{t('Est. Date')}: <b>—</b></span>
                            <span>{t('Est. Nr.')}: <b>—</b></span>
                            <span>{t('Currency')}: <b>—</b></span>
                            <span>{t('Exchange')}: <b>0</b></span>
                        </div>
                        <div style={{ marginTop: '8px', fontSize: '0.78rem', color: 'var(--color-grey-dark2)', fontStyle: 'italic' }}>
                            * {t('MultipleValuesHint')}
                        </div>
                    </div>
                    <div style={readonlyBoxStyle}>
                        <span style={{ fontSize: '0.8rem' }}>{t('ApprovedCostAutoFilled')}</span>
                    </div>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>{t('Approved Date')}</label>
                        <input type="date" style={inputStyle} value={approvedDate}
                            onChange={e => setApprovedDate(e.target.value)} />
                    </div>
                </div>
            }
            footer={
                <span style={footerStyle}>
                    <M_Button normal hoverEffect label={t('ButtonCancel')}
                        backgroundColor={'var(--color-white)'} borderColor={'var(--color-grey-dark2)'}
                        labelColor={'var(--color-grey-dark2)'} onClick={onClose} />
                    <M_Button normal hoverEffect label={t('Approve All')}
                        backgroundColor={'var(--color-green)'} borderColor={'var(--color-green)'}
                        labelColor={'var(--color-white)'} onClick={handleApprove} />
                </span>
            }
        />
    )
}

// ── Check Cost Modal (individual) ─────────────────────────────────────────────
const CheckCostModal = ({ open, details, onClose, onDone, ctx, t }) => {
    const [index, setIndex] = useState(0)
    const [supplierRef, setSupplierRef] = useState('')

    React.useEffect(() => {
        if (open) { setIndex(0); setSupplierRef('') }
    }, [open])

    if (!open || !details.length) return null
    const current = details[index]
    const isLast = index === details.length - 1

    const handleNext = async () => {
        if (!supplierRef.trim()) { ctx.toastMessages('error', '', t('Missing required fields')); return }
        ctx.setIsLoad(true)
        const res = await detailService.patch(`CheckCost/${current.Id}`, { SupplierRef: supplierRef })
        ctx.setIsLoad(false)
        if (res?.name === 'AxiosError') { ctx.toastMessages('error', res.response?.data?.detail || res.message, t('Error')); return }
        ctx.toastMessages('success', '', t('CostChecked'))
        if (isLast) { onDone(); return }
        setIndex(i => i + 1)
        setSupplierRef('')
    }

    return (
        <M_MessageBoard
            open={open} onClose={onClose}
            title={<span>{t('Check Cost')} — {index + 1}/{details.length}</span>}
            body={
                <div style={modalBodyStyle}>
                    <div style={readonlyBoxStyle}>
                        <span>{t('Equipment')}: <b>{current.EquipmentPrefix}{current.EquipmentNumber}-{current.EquipmentCheckDigit}</b></span>
                        <span>{t('Service')}: <b>{current.ServiceTypeCode}</b></span>
                        <span>{t('Est. Cost')}: <b>{current.EstimatedCost}</b></span>
                        <span>{t('Supplier')}: <b>{current.SupplierName}</b></span>
                    </div>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>{t('Supplier Ref')}</label>
                        <input style={inputStyle} value={supplierRef}
                            onChange={e => setSupplierRef(e.target.value)}
                            placeholder={t('Invoice Number')} />
                    </div>
                </div>
            }
            footer={
                <span style={footerStyle}>
                    <M_Button normal hoverEffect label={t('ButtonCancel')}
                        backgroundColor={'var(--color-white)'} borderColor={'var(--color-grey-dark2)'}
                        labelColor={'var(--color-grey-dark2)'} onClick={onClose} />
                    <M_Button normal hoverEffect
                        label={isLast ? t('Check') : `${t('Check')} & ${t('Next')}`}
                        backgroundColor={'var(--color-blue)'} borderColor={'var(--color-blue)'}
                        labelColor={'var(--color-white)'} onClick={handleNext} />
                </span>
            }
        />
    )
}

// ── Check All Costs Modal ─────────────────────────────────────────────────────
const CheckAllCostsModal = ({ open, details, onClose, onDone, ctx, t }) => {
    const [supplierRef, setSupplierRef] = useState('')

    React.useEffect(() => {
        if (open) setSupplierRef('')
    }, [open])

    if (!open || !details.length) return null

    const handleCheck = async () => {
        if (!supplierRef.trim()) { ctx.toastMessages('error', '', t('Missing required fields')); return }
        ctx.setIsLoad(true)
        const res = await detailService.patch(`CheckAllCosts/`, { IdList: details.map(d => d.Id), SupplierRef: supplierRef })
        ctx.setIsLoad(false)
        if (res?.name === 'AxiosError') { ctx.toastMessages('error', res.response?.data?.detail || res.message, t('Error')); return }
        ctx.toastMessages('success', '', t('AllCostsChecked'))
        onDone()
    }

    return (
        <M_MessageBoard
            open={open} onClose={onClose}
            title={`${t('Check All Costs')} — ${details.length} ${t('selected')}`}
            body={
                <div style={{ ...modalBodyStyle, minWidth: '480px' }}>
                    <div style={readonlyBoxStyle}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                            <span>{t('Prefix')}: <b>*</b></span>
                            <span>{t('Number')}: <b>*</b></span>
                            <span>{t('Move')}: <b>*</b></span>
                            <span>{t('Mov. Key')}: <b>0</b></span>
                            <span>{t('Cost Key')}: <b>0</b></span>
                        </div>
                        <div style={{ borderTop: '1px solid var(--color-grey-dark2)', margin: '8px 0' }} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <span>{t('Service Code')}: <b>*</b></span>
                            <span>{t('Service Name')}: <b>—</b></span>
                            <span>{t('Supplier')}: <b>*</b></span>
                            <span>{t('Est. Cost')}: <b>—</b></span>
                            <span>{t('Est. Date')}: <b>—</b></span>
                            <span>{t('Est. Nr.')}: <b>—</b></span>
                            <span>{t('Currency')}: <b>—</b></span>
                            <span>{t('Exchange')}: <b>0</b></span>
                            <span>{t('Approved Cost')}: <b>—</b></span>
                            <span>{t('Approved Date')}: <b>—</b></span>
                        </div>
                        <div style={{ marginTop: '8px', fontSize: '0.78rem', color: 'var(--color-grey-dark2)', fontStyle: 'italic' }}>
                            * {t('MultipleValuesHint')}
                        </div>
                    </div>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>{t('Supplier Ref')}</label>
                        <input style={inputStyle} value={supplierRef}
                            onChange={e => setSupplierRef(e.target.value)}
                            placeholder={t('Invoice Number')} />
                    </div>
                </div>
            }
            footer={
                <span style={footerStyle}>
                    <M_Button normal hoverEffect label={t('ButtonCancel')}
                        backgroundColor={'var(--color-white)'} borderColor={'var(--color-grey-dark2)'}
                        labelColor={'var(--color-grey-dark2)'} onClick={onClose} />
                    <M_Button normal hoverEffect label={t('Check All')}
                        backgroundColor={'var(--color-blue)'} borderColor={'var(--color-blue)'}
                        labelColor={'var(--color-white)'} onClick={handleCheck} />
                </span>
            }
        />
    )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export const EquipmentCostDetailList = ({ roles }) => {
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)

    const [selectedRows, setSelectedRows] = useState([])
    const [refresh, setRefresh] = useState(false)
    const [totals, setTotals] = useState(null)
    const [totalsLoading, setTotalsLoading] = useState(false)
    const [modal, setModal] = useState(null)
    const [detailModal, setDetailModal] = useState(null)
    const [activeFilter, setActiveFilter] = useState('')
    const isFirstRender = useRef(true)

    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return }
        setRefresh(true)
    }, [activeFilter])

    const closeModal = () => setModal(null)
    const closeDetailModal = () => setDetailModal(null)

    const onDone = () => {
        closeModal()
        setRefresh(true)
        setSelectedRows([])
        setTotals(null)
    }

    const onDetailSaved = () => {
        closeDetailModal()
        setRefresh(true)
    }

    const guardSelection = (action) => {
        if (!selectedRows.length) { ctx.toastMessages('error', '', t('SelectedLine')); return }
        action()
    }

    const handleRowSelect = useCallback(async (rows) => {
        setSelectedRows(rows)
        if (!rows || rows.length === 0) { setTotals(null); return }
        setTotalsLoading(true)
        const ids = rows.map(r => r.Id)
        const res = await detailService.postTo('CalculateTotals', ids)
        setTotalsLoading(false)
        if (res?.name === 'AxiosError') { ctx.toastMessages('error', res.message, t('Error')); return }
        setTotals(res)
    }, [ctx, t])

    return (
        <div style={{ display: 'flex', gap: '12px', height: '100%' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <M_Table
                    entityName={'EquipmentCostDetail'}
                    api='secondary'
                    showTitle={true}
                    title={'Equipment Cost Details'}
                    roleName={'usersGroups'}
                    rolePermissions={roles}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    onPassLine={handleRowSelect}
                    removeContextItems={['Create', 'Copy', 'Edit']}
                    onRowDoubleClick={(row) => setDetailModal(row)}
                    FILTER={activeFilter || undefined}
                    filterComponent={<EquipmentCostDetailFilter onFilterChange={setActiveFilter} />}
                    replaceFunctions={new Map([
                        ['View', (row) => setDetailModal(row)],
                        // ['Delete', (rows) => {
                        //     if (!rows?.length) { ctx.toastMessages('error', '', t('SelectedLine')); return }
                        //     ctx.toastMessages('error', t('CannotDeleteCostDetail'), '')
                        // }],
                    ])}
                    lineContext={[
                        { separator: true },
                        {
                            label: t('Update All Costs'),
                            icon: <i className='pi pi-pencil' style={{ marginRight: '0.5rem', color: 'var(--color-blue)' }} />,
                            command: () => guardSelection(() => setModal('updateAll'))
                        },
                        { separator: true },
                        {
                            label: t('Approve Cost'),
                            icon: <i className='pi pi-check' style={{ marginRight: '0.5rem', color: 'var(--color-green)' }} />,
                            command: () => guardSelection(() => setModal('approveCost'))
                        },
                        {
                            label: t('Approve All Costs'),
                            icon: <i className='pi pi-check-circle' style={{ marginRight: '0.5rem', color: 'var(--color-green)' }} />,
                            command: () => guardSelection(() => setModal('approveAll'))
                        },
                        { separator: true },
                        {
                            label: t('Check Cost'),
                            icon: <i className='pi pi-file-check' style={{ marginRight: '0.5rem', color: 'var(--color-blue)' }} />,
                            command: () => guardSelection(() => setModal('checkCost'))
                        },
                        {
                            label: t('Check All Costs'),
                            icon: <i className='pi pi-verified' style={{ marginRight: '0.5rem', color: 'var(--color-blue)' }} />,
                            command: () => guardSelection(() => setModal('checkAll'))
                        },
                    ]}
                    extraButtons={[
                        {
                            element: 'default',
                            label: t('Update All'),
                            icon: <i className='pi pi-pencil iconTable' style={{ color: 'var(--color-blue)' }} />,
                            needLine: true,
                            command: () => guardSelection(() => setModal('updateAll'))
                        },
                        {
                            element: 'default',
                            label: t('Approve Cost'),
                            icon: <i className='pi pi-check iconTable' style={{ color: 'var(--color-green)' }} />,
                            needLine: true,
                            command: () => guardSelection(() => setModal('approveCost'))
                        },
                        {
                            element: 'default',
                            label: t('Approve All'),
                            icon: <i className='pi pi-check-circle iconTable' style={{ color: 'var(--color-green)' }} />,
                            needLine: true,
                            command: () => guardSelection(() => setModal('approveAll'))
                        },
                        {
                            element: 'default',
                            label: t('Check Cost'),
                            icon: <i className='pi pi-file-check iconTable' style={{ color: 'var(--color-blue)' }} />,
                            needLine: true,
                            command: () => guardSelection(() => setModal('checkCost'))
                        },
                        {
                            element: 'default',
                            label: t('Check All'),
                            icon: <i className='pi pi-verified iconTable' style={{ color: 'var(--color-blue)' }} />,
                            needLine: true,
                            command: () => guardSelection(() => setModal('checkAll'))
                        },
                    ]}
                />
            </div>

            <TotalsPanel totals={totals} loading={totalsLoading} t={t} />

            <DetailModal
                open={!!detailModal}
                detail={detailModal}
                onClose={closeDetailModal}
                onSave={onDetailSaved}
                ctx={ctx}
                t={t}
            />
            <UpdateAllCostsModal
                open={modal === 'updateAll'}
                details={selectedRows}
                onClose={closeModal}
                onDone={onDone}
                ctx={ctx}
                t={t}
            />
            <ApproveCostModal
                open={modal === 'approveCost'}
                details={selectedRows}
                onClose={closeModal}
                onDone={onDone}
                ctx={ctx}
                t={t}
            />
            <ApproveAllCostsModal
                open={modal === 'approveAll'}
                details={selectedRows}
                onClose={closeModal}
                onDone={onDone}
                ctx={ctx}
                t={t}
            />
            <CheckCostModal
                open={modal === 'checkCost'}
                details={selectedRows}
                onClose={closeModal}
                onDone={onDone}
                ctx={ctx}
                t={t}
            />
            <CheckAllCostsModal
                open={modal === 'checkAll'}
                details={selectedRows}
                onClose={closeModal}
                onDone={onDone}
                ctx={ctx}
                t={t}
            />
        </div>
    )
}